export interface User {
  id: number;
  username: string;
  name?: string | null;
  email?: string | null;
  role: "admin" | "gerente" | "funcionario" | "cozinha" | "marketing" | "user";
  /** null = admin (acesso total). Array vazio = nenhuma aba. */
  allowed_tabs?: string[] | null;
}

export interface Insumo {
  id: number;
  nome: string;
  categoria: string;
  unidade: string;
  quantidadeAtual: number;
  estoqueMinimo: number;
  observacoes?: string | null;
  observacoes_compra?: string | null;
  fornecedor_nome?: string | null;
  fornecedor_telefone?: string | null;
  fornecedor_preco?: number | null;
  valorAtual?: number | null;
}

export interface SugestaoFornecedor {
  nome: string;
  telefone: string;
  usado_em?: number;
  count?: number;
}

export interface ConferenciaAlteracao {
  tipo: "insumo" | "produto";
  item_id: number;
  item_nome: string;
  unidade: string;
  quantidade_antes: number;
  quantidade_depois: number;
}

export interface Conferencia {
  id: number;
  usuario_id: number;
  usuario_nome: string;
  data_conferencia: string;
  observacoes: string | null;
  alteracoes?: ConferenciaAlteracao[];
}

export interface Produto {
  id: number;
  nome: string;
  ncm?: string | null;
  possui_composicao: boolean;
  exibir_no_cardapio?: boolean;
  controla_estoque?: boolean;
  unidade_venda?: string;
  ativo: string;
  quantidadeAtual: number;
  preco_venda?: number | null;
  quantidade_minima?: number;
  observacoes_compra?: string | null;
  descricao?: string | null;
  imagem_url?: string | null;
  fornecedor_nome?: string | null;
  fornecedor_telefone?: string | null;
  fornecedor_preco?: number | null;
  valorAtual?: number | null;
  unidade?: string;
  categoria?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProdutoComposicaoItem {
  produto_id: number;
  insumo_id: number;
  quantidade: number;
  insumo_nome?: string;
  unidade?: string;
  unidade_base?: string;
}

const defaultOptions: RequestInit = {
  credentials: "include",
  headers: {
    "Content-Type": "application/json",
  },
};

/** Cancelamento intencional de fetch (AbortController) — não tratar como erro de app. */
export function isAbortError(error: unknown): boolean {
  if (error == null || typeof error !== "object") return false;
  const name = (error as { name?: unknown }).name;
  if (name === "AbortError") return true;
  if (typeof DOMException !== "undefined" && error instanceof DOMException && error.name === "AbortError") {
    return true;
  }
  return false;
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = "ApiError";
  }
}

async function produtoSaveOptions(payload: object, image?: File | null): Promise<RequestInit> {
  if (!image) return { ...defaultOptions, method: "POST", body: JSON.stringify(payload) };
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(image.type) || image.size === 0) {
    throw new ApiError("Imagem invalida. Use JPG, PNG ou WebP.", 422);
  }
  if (image.size > 5 * 1024 * 1024) {
    throw new ApiError("Imagem muito grande (max. 5 MB).", 413);
  }
  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const value = reader.result;
      if (typeof value !== "string" || !value.includes(";base64,")) {
        reject(new ApiError("Nao foi possivel ler a imagem selecionada.", 422));
        return;
      }
      resolve(value.slice(value.indexOf(",") + 1));
    };
    reader.onerror = () => reject(new ApiError("Nao foi possivel ler a imagem selecionada. Selecione o arquivo novamente.", 422));
    reader.onabort = () => reject(new ApiError("Leitura da imagem cancelada. Tente novamente.", 422));
    reader.readAsDataURL(image);
  });
  return {
    ...defaultOptions,
    method: "POST",
    body: JSON.stringify({ ...payload, imagem_upload: { mime: image.type, base64 } }),
  };
}

async function handleProdutoSaveJson<T>(res: Response): Promise<T> {
  return handleJson<T>(res, () => {
    const messages: Record<number, string> = {
      401: "Sessao expirada. Entre novamente para salvar o produto.",
      403: "O servidor ou a hospedagem recusou a requisicao de produto/imagem e retornou uma resposta sem JSON. Verifique o bloqueio com o suporte da hospedagem.",
      413: "Imagem acima do limite de upload configurado no servidor.",
      404: "Endpoint de produto nao encontrado no servidor. Verifique o deploy da API.",
      422: "Imagem ou dados do produto invalidos.",
      429: "Muitos envios de imagem. Aguarde um minuto e tente novamente.",
      500: "Falha interna do servidor ao salvar o produto/imagem. Verifique os logs PHP.",
    };
    return new ApiError(`${messages[res.status] ?? "O servidor nao retornou uma confirmacao JSON valida do produto/imagem."} (HTTP ${res.status})`, res.status);
  });
}

function confirmedProduto(data: { produto?: Produto }, image: File | null | undefined, status: number): Produto {
  if (!data?.produto || !Number.isInteger(data.produto.id) || data.produto.id <= 0 ||
      (image && !data.produto.imagem_url)) {
    throw new ApiError("A API nao confirmou o salvamento completo do produto/imagem. Verifique a resposta do servidor.", status);
  }
  return data.produto;
}

async function handleJson<T>(res: Response, invalidResponse?: () => Error): Promise<T> {
  const text = await res.text();
  const trimmed = text.trim();

  // Resposta vazia (204 No Content ou body vazio): sucesso sem corpo
  if (!trimmed) {
    if (invalidResponse) throw invalidResponse();
    if (res.ok) return {} as T;
    throw new ApiError("Erro na requisição", res.status);
  }

  let data: unknown;
  try {
    data = JSON.parse(trimmed);
  } catch {
    if (invalidResponse) throw invalidResponse();
    if (res.ok) return {} as T;
    throw new ApiError("Resposta inválida do servidor", res.status);
  }

  if (!res.ok || (data && typeof data === "object" && "ok" in data && (data as { ok?: boolean }).ok === false)) {
    const obj = data && typeof data === "object" ? (data as Record<string, unknown>) : null;
    const serverMsg =
      obj && typeof obj.error === "string" && obj.error.trim() !== ""
        ? obj.error.trim()
        : obj && typeof obj.message === "string" && obj.message.trim() !== ""
          ? obj.message.trim()
          : "";
    const base = serverMsg || "Erro na requisição";
    const withStatus = !res.ok && res.status ? `${base} (HTTP ${res.status})` : base;
    throw new ApiError(withStatus, res.status);
  }
  return data as T;
}

export const api = {
  async login(username: string, password: string): Promise<User> {
    const res = await fetch("/api/auth/login.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
    const text = await res.text();
    const trimmed = text.trim();

    if (!trimmed) {
      if (res.ok) throw new Error("Resposta inválida do servidor");
      throw new Error("Erro na requisição");
    }

    let data: unknown;
    try {
      data = JSON.parse(trimmed);
    } catch {
      throw new Error(res.ok ? "Resposta inválida do servidor" : "Erro na requisição");
    }

    const obj = data && typeof data === "object" ? (data as Record<string, unknown>) : null;
    const serverMsg =
      obj && typeof obj.error === "string" && obj.error.trim() !== ""
        ? obj.error.trim()
        : obj && typeof obj.message === "string" && obj.message.trim() !== ""
          ? obj.message.trim()
          : "";

    if (!res.ok || (obj && obj.ok === false)) {
      throw new Error(serverMsg || "Erro na requisição");
    }

    return (data as { user: User }).user;
  },

  async me(): Promise<User | null> {
    const res = await fetch("/api/auth/me.php", {
      ...defaultOptions,
      method: "GET",
      cache: "no-store",
    });
    if (res.status === 401) return null;
    const data = await handleJson<{ user: User }>(res);
    return data.user;
  },

  async logout(): Promise<void> {
    const res = await fetch("/api/auth/logout.php", {
      ...defaultOptions,
      method: "POST",
    });
    await handleJson(res);
  },

  async listarCategorias(): Promise<{ id: number; nome: string; createdAt: string | null }[]> {
    const res = await fetch("/api/categorias/list.php", {
      ...defaultOptions,
      method: "GET",
    });
    const data = await handleJson<{ categorias: { id: number; nome: string; createdAt: string | null }[] }>(res);
    return data.categorias;
  },

  async excluirCategoria(
    id: number,
    nome: string,
  ): Promise<{ nome: string; categorias: { id: number; nome: string; createdAt: string | null }[] }> {
    const res = await fetch("/api/categorias/delete.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ id, nome }),
    });
    const data = await handleJson<{
      message: string;
      nome: string;
      categorias: { id: number; nome: string; createdAt: string | null }[];
    }>(res);
    return { nome: data.nome, categorias: data.categorias ?? [] };
  },

  async listarInsumos(options?: { signal?: AbortSignal }): Promise<Insumo[]> {
    const res = await fetch("/api/insumos/list.php", {
      ...defaultOptions,
      method: "GET",
      signal: options?.signal,
    });
    const data = await handleJson<{ insumos: Insumo[] }>(res);
    return data.insumos;
  },

  async listarBaixoEstoque(): Promise<Insumo[]> {
    const res = await fetch("/api/insumos/baixo-estoque.php", {
      ...defaultOptions,
      method: "GET",
    });
    const data = await handleJson<{ insumos: Insumo[] }>(res);
    return data.insumos;
  },

  async atualizarQuantidade(id: number, quantidade: number): Promise<void> {
    const res = await fetch("/api/insumos/update-quantidade.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ id, quantidade }),
    });
    await handleJson(res);
  },

  async registrarEntradas(itens: Array<{ tipo: 'produto' | 'insumo'; id: number; quantidadeLancada: number; unidadeLancamento: string; valorPago?: number | null }>): Promise<{ itens: Array<{ tipo: string; id: number; quantidadeAtual: number; valorAtual: number | null }> }> {
    const res = await fetch("/api/estoque/entradas.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ itens }),
    });
    const data = await handleJson<{ itens: Array<{ tipo: string; id: number; quantidadeAtual: number; valorAtual: number | null }> }>(res);
    return data;
  },

  async atualizarEstoqueMinimo(id: number, estoqueMinimo: number): Promise<void> {
    const res = await fetch("/api/insumos/update-estoque-minimo.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ id, estoqueMinimo }),
    });
    await handleJson(res);
  },

  async criarInsumo(payload: {
    nome: string;
    categoria: string;
    unidade: string;
    estoqueMinimo: number;
    valor_atual?: number | null;
  }): Promise<Insumo> {
    const res = await fetch("/api/insumos/create.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify(payload),
    });
    const data = await handleJson<{ insumo: Insumo }>(res);
    return data.insumo;
  },

  async ultimaConferencia(): Promise<Conferencia | null> {
    const res = await fetch("/api/conferencias/ultima.php", {
      ...defaultOptions,
      method: "GET",
    });
    const data = await handleJson<{ conferencia: Conferencia | null }>(res);
    return data.conferencia;
  },

  async criarConferencia(observacoes?: string, alteracoes?: ConferenciaAlteracao[]): Promise<Conferencia> {
    const res = await fetch("/api/conferencias/create.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ observacoes, alteracoes: alteracoes ?? [] }),
    });
    const data = await handleJson<{ conferencia: Conferencia }>(res);
    return data.conferencia;
  },

  async listarConferencias(params?: { start?: string; end?: string }): Promise<Conferencia[]> {
    const q = new URLSearchParams();
    if (params?.start) q.set("start", params.start);
    if (params?.end) q.set("end", params.end);
    const url = q.toString() ? `/api/conferencias/list.php?${q.toString()}` : "/api/conferencias/list.php";
    const res = await fetch(url, {
      ...defaultOptions,
      method: "GET",
    });
    const data = await handleJson<{ conferencias: Conferencia[] }>(res);
    const list = data.conferencias ?? [];
    return list.map((c: Conferencia & { alteracoes?: ConferenciaAlteracao[] }) => ({
      id: c.id,
      usuario_id: c.usuario_id,
      usuario_nome: c.usuario_nome ?? "",
      data_conferencia: c.data_conferencia ?? "",
      observacoes: c.observacoes ?? null,
      alteracoes: c.alteracoes ?? [],
    }));
  },

  async excluirConferencia(id: number): Promise<void> {
    const res = await fetch("/api/conferencias/delete.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ id }),
    });
    await handleJson(res);
  },

  async atualizarInsumo(id: number, payload: {
    nome: string;
    categoria: string;
    unidade: string;
    estoqueMinimo: number;
    observacoes?: string | null;
    valor_atual?: number | null;
  }): Promise<Insumo> {
    const res = await fetch("/api/insumos/update.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ id, ...payload }),
    });
    const data = await handleJson<{ insumo: Insumo }>(res);
    return data.insumo;
  },

  async excluirInsumo(id: number): Promise<void> {
    const res = await fetch("/api/insumos/delete.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ id }),
    });
    await handleJson(res);
  },

  async atualizarObservacoesCompra(id: number, observacoes_compra: string | null): Promise<void> {
    const res = await fetch("/api/insumos/update-observacoes-compra.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ id, observacoes_compra }),
    });
    await handleJson(res);
  },

  async atualizarObservacoesCompraProduto(id: number, observacoes_compra: string | null): Promise<void> {
    const res = await fetch("/api/produtos/update-observacoes-compra.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ id, observacoes_compra }),
    });
    await handleJson(res);
  },

  async listarSugestoesFornecedores(): Promise<SugestaoFornecedor[]> {
    const res = await fetch("/api/fornecedores/sugestoes.php", { ...defaultOptions, method: "GET" });
    const data = await handleJson<{ fornecedores: SugestaoFornecedor[] }>(res);
    return data.fornecedores ?? [];
  },

  async sugestoesFornecedores(): Promise<SugestaoFornecedor[]> {
    return this.listarSugestoesFornecedores();
  },

  async upsertFornecedorInsumo(insumo_id: number, payload: {
    fornecedor_nome: string;
    fornecedor_telefone: string | null;
    fornecedor_preco: number | null;
  }): Promise<void> {
    const res = await fetch("/api/fornecedores/upsert-insumo.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ insumo_id, ...payload }),
    });
    await handleJson(res);
  },

  async upsertFornecedorProduto(produto_id: number, payload: {
    fornecedor_nome: string;
    fornecedor_telefone: string | null;
    fornecedor_preco: number | null;
  }): Promise<void> {
    const res = await fetch("/api/fornecedores/upsert-produto.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ produto_id, ...payload }),
    });
    await handleJson(res);
  },

  /** Item de desperdício (carrinho) */
  async listarDesperdicios(params?: { start?: string; end?: string; responsavel?: string; tipo?: 'consumo_interno' | 'vencimento' | 'brinde_propaganda' | 'cortesia_avaliacao' | 'avaria' }): Promise<DesperdicioListItem[]> {
    const q = new URLSearchParams();
    if (params?.start) q.set("start", params.start);
    if (params?.end) q.set("end", params.end);
    if (params?.responsavel) q.set("responsavel", params.responsavel);
    if (params?.tipo) q.set("tipo", params.tipo);
    const url = q.toString() ? `/api/desperdicios/list.php?${q.toString()}` : "/api/desperdicios/list.php";
    const res = await fetch(url, { ...defaultOptions, method: "GET" });
    const data = await handleJson<{ ok?: boolean; desperdicios?: unknown[] }>(res);
    const raw = Array.isArray(data?.desperdicios) ? data.desperdicios : [];
    return raw.map((d: unknown) => {
      const r = d as Record<string, unknown>;
      const itensRaw = (r.itens as unknown[]) ?? [];
      const itens = itensRaw.map((it: unknown) => {
        const i = it as Record<string, unknown>;
        return {
          produto_id: Number(i.produto_id) ?? 0,
          nome_produto: String(i.nome_produto ?? i.nome ?? ''),
          quantidade: Number(i.quantidade) ?? 0,
          preco_unitario: Number(i.preco_unitario) ?? 0,
          subtotal: Number(i.subtotal) ?? 0,
        };
      });
      return {
        id: Number(r.id) || 0,
        tipo_item: (r.tipo_item === 'carrinho' ? 'carrinho' : r.tipo_item === 'produto' ? 'produto' : 'insumo') as 'insumo' | 'produto' | 'carrinho',
        motivo: String(r.motivo ?? ''),
        observacao: r.observacao != null && r.observacao !== '' ? String(r.observacao) : null,
        createdAt: String(r.createdAt ?? ''),
        total: Number(r.total) ?? 0,
        responsavel_nome: r.responsavel_nome != null ? String(r.responsavel_nome) : null,
        itens,
      };
    });
  },

  async listarResponsaveisDesperdicio(): Promise<string[]> {
    const res = await fetch("/api/desperdicios/responsaveis.php", { ...defaultOptions, method: "GET" });
    const data = await handleJson<{ ok?: boolean; responsaveis?: unknown[] }>(res);
    const raw = Array.isArray(data?.responsaveis) ? data.responsaveis : [];
    return raw.map((x) => String(x));
  },

  async registrarDesperdicio(payload: {
    tipo_item: 'insumo' | 'produto';
    item_id: number;
    quantidade: number;
    motivo: 'consumo_interno' | 'vencimento' | 'brinde_propaganda' | 'cortesia_avaliacao' | 'avaria';
    observacao?: string | null;
  }): Promise<void> {
    const res = await fetch("/api/desperdicios/create.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify(payload),
    });
    await handleJson(res);
  },

  async excluirDesperdicio(id: number): Promise<void> {
    const res = await fetch("/api/desperdicios/delete.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ id }),
    });
    await handleJson(res);
  },

  async listarUsuarios(): Promise<{ id: number; username: string; name: string | null; email?: string | null; telefone?: string | null; role: string; allowed_tabs: string[] | null; created_at: string }[]> {
    const res = await fetch("/api/users/list.php", {
      ...defaultOptions,
      method: "GET",
    });
    const data = await handleJson<{ users: { id: number; username: string; name: string | null; email?: string | null; telefone?: string | null; role: string; allowed_tabs: string[] | null; created_at: string }[] }>(res);
    return data.users;
  },

  async atualizarTabsUsuario(userId: number, allowedTabs: string[] | null): Promise<void> {
    const res = await fetch("/api/users/update-tabs.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ user_id: userId, allowed_tabs: allowedTabs }),
    });
    await handleJson(res);
  },

  async criarUsuario(payload: {
    username: string;
    password: string;
    name?: string | null;
    email?: string | null;
    telefone?: string | null;
    role: string;
    allowed_tabs?: string[] | null;
  }): Promise<{ id: number; username: string; name: string | null; email?: string | null; telefone?: string | null; role: string; allowed_tabs: string[] | null }> {
    const res = await fetch("/api/users/create.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify(payload),
    });
    const data = await handleJson<{ user: { id: number; username: string; name: string | null; email?: string | null; telefone?: string | null; role: string; allowed_tabs: string[] | null } }>(res);
    return data.user;
  },

  async atualizarUsuario(payload: {
    user_id: number;
    name?: string | null;
    email?: string | null;
    telefone?: string | null;
    role?: string;
    password?: string;
    allowed_tabs?: string[] | null;
  }): Promise<{ id: number; username: string; name: string | null; email?: string | null; telefone?: string | null; role: string; allowed_tabs: string[] | null }> {
    const res = await fetch("/api/users/update.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify(payload),
    });
    const data = await handleJson<{ user: { id: number; username: string; name: string | null; email?: string | null; telefone?: string | null; role: string; allowed_tabs: string[] | null } }>(res);
    return data.user;
  },

  async excluirUsuario(userId: number): Promise<void> {
    const res = await fetch("/api/users/delete.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ user_id: userId }),
    });
    await handleJson(res);
  },

  async listarProdutos(soAtivos?: boolean, options?: { signal?: AbortSignal }): Promise<Produto[]> {
    const url = soAtivos ? "/api/produtos/list.php?só_ativos=1" : "/api/produtos/list.php";
    const res = await fetch(url, { ...defaultOptions, method: "GET", signal: options?.signal });
    const data = await handleJson<{ produtos: Produto[] }>(res);
    return data.produtos;
  },

  async obterProdutosVendasQuantidade(): Promise<Record<number, number>> {
    const res = await fetch("/api/produtos/vendas-quantidade.php", { ...defaultOptions, method: "GET" });
    const data = await handleJson<{ totais: Record<string, number> }>(res);
    const out: Record<number, number> = {};
    for (const [key, value] of Object.entries(data.totais ?? {})) {
      const id = parseInt(key, 10);
      if (id > 0) out[id] = Number(value) || 0;
    }
    return out;
  },

  async obterProduto(id: number): Promise<Produto & { composicao?: ProdutoComposicaoItem[] }> {
    const res = await fetch(`/api/produtos/get.php?id=${id}`, { ...defaultOptions, method: "GET" });
    const data = await handleJson<{ produto: Produto & { composicao?: ProdutoComposicaoItem[] } }>(res);
    return data.produto;
  },

  async atualizarProduto(id: number, payload: {
    nome: string;
    categoria: string;
    possui_composicao: boolean;
    exibir_no_cardapio?: boolean;
    controla_estoque?: boolean;
    unidade_venda?: string;
    preco_venda?: number | string | null;
    quantidade_minima?: number | string;
    observacoes_compra?: string | null;
    descricao?: string | null;
    imagem_url?: string | null;
    ncm?: string | null;
    valor_atual?: number | null;
    composicao?: { insumo_id: number; quantidade: number; unidade?: string }[];
  }, image?: File | null): Promise<Produto> {
    const res = await fetch("/api/produtos/update.php", await produtoSaveOptions({ id, ...payload }, image));
    const data = await handleProdutoSaveJson<{ produto: Produto }>(res);
    return confirmedProduto(data, image, res.status);
  },

  async listarProdutosBaixoEstoque(): Promise<Produto[]> {
    const res = await fetch("/api/produtos/list-baixo-estoque.php", { ...defaultOptions, method: "GET" });
    const data = await handleJson<{ produtos: Produto[] }>(res);
    return data.produtos;
  },

  async listarProdutosSemComposicao(): Promise<Produto[]> {
    const res = await fetch("/api/produtos/list-sem-composicao.php", { ...defaultOptions, method: "GET" });
    const data = await handleJson<{ produtos: Produto[] }>(res);
    return data.produtos;
  },

  async criarProduto(payload: {
    nome: string;
    categoria: string;
    possui_composicao: boolean;
    exibir_no_cardapio?: boolean;
    controla_estoque?: boolean;
    unidade_venda?: string;
    estoque_inicial?: number | string | null;
    preco_venda?: number | string | null;
    quantidade_minima?: number | string;
    observacoes_compra?: string | null;
    descricao?: string | null;
    imagem_url?: string | null;
    ncm?: string | null;
    valor_atual?: number | null;
    composicao?: { insumo_id: number; quantidade: number; unidade?: string }[];
  }, image?: File | null): Promise<Produto> {
    const res = await fetch("/api/produtos/create.php", await produtoSaveOptions(payload, image));
    const data = await handleProdutoSaveJson<{ produto: Produto }>(res);
    return confirmedProduto(data, image, res.status);
  },

  async uploadImagemProduto(produtoId: number, file: File): Promise<{ imagem_url: string }> {
    const res = await fetch("/api/produtos/upload-imagem.php", await produtoSaveOptions({ produto_id: produtoId }, file));
    const data = await handleProdutoSaveJson<{ imagem_url: string }>(res);
    if (typeof data?.imagem_url !== "string" || !data.imagem_url) {
      throw new ApiError("A API nao confirmou o armazenamento da imagem.", res.status);
    }
    return data;
  },

  async removerImagemProduto(produtoId: number): Promise<void> {
    const res = await fetch("/api/produtos/remove-imagem.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ produto_id: produtoId }),
    });
    await handleJson(res);
  },

  async atualizarQuantidadeProduto(id: number, quantidade: number): Promise<void> {
    const res = await fetch("/api/produtos/update-quantidade.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ id, quantidade }),
    });
    await handleJson(res);
  },

  async getComposicao(produtoId: number, options?: { signal?: AbortSignal }): Promise<ProdutoComposicaoItem[]> {
    const res = await fetch(`/api/produtos/composicao.php?produto_id=${produtoId}`, {
      ...defaultOptions,
      method: "GET",
      signal: options?.signal,
    });
    const data = await handleJson<{ composicao: ProdutoComposicaoItem[] }>(res);
    if (!Array.isArray(data.composicao) || data.composicao.some((item) =>
      !item || typeof item !== "object" ||
      !Number.isInteger(Number(item.insumo_id)) || Number(item.insumo_id) <= 0 ||
      item.quantidade == null || !Number.isFinite(Number(item.quantidade)) || Number(item.quantidade) <= 0,
    )) {
      throw new ApiError("Composicao do produto invalida ou incompleta", 422);
    }
    return data.composicao.map((item) => ({ ...item, insumo_id: Number(item.insumo_id), quantidade: Number(item.quantidade) }));
  },

  async salvarComposicao(produtoId: number, composicao: { insumo_id: number; quantidade: number }[]): Promise<void> {
    const res = await fetch("/api/produtos/composicao.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ produto_id: produtoId, composicao }),
    });
    await handleJson(res);
  },

  async excluirProduto(id: number): Promise<void> {
    const res = await fetch("/api/produtos/delete.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ id }),
    });
    await handleJson(res);
  },

  async listarVendas(
    params?: { start?: string; end?: string },
    options?: { signal?: AbortSignal },
  ): Promise<Venda[]> {
    const q = new URLSearchParams();
    if (params?.start) q.set("start", params.start);
    if (params?.end) q.set("end", params.end);
    const url = q.toString() ? `/api/vendas/list.php?${q.toString()}` : "/api/vendas/list.php";
    const res = await fetch(url, { ...defaultOptions, method: "GET", signal: options?.signal });
    const data = await handleJson<{ vendas: Venda[] }>(res);
    return data.vendas ?? [];
  },

  async listarVendasFiscais(
    params: { start: string; end: string },
    options?: { signal?: AbortSignal },
  ): Promise<Venda[]> {
    const q = new URLSearchParams();
    q.set("start", params.start);
    q.set("end", params.end);
    const res = await fetch(`/api/vendas/list-fiscal.php?${q.toString()}`, {
      ...defaultOptions,
      method: "GET",
      signal: options?.signal,
    });
    const data = await handleJson<{ vendas: Venda[] }>(res);
    return data.vendas ?? [];
  },

  async excluirVenda(id: number): Promise<void> {
    const res = await fetch("/api/vendas/delete.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ id }),
    });
    await handleJson(res);
  },

  // --- Despesas ---
  async listarDespesas(mes: string): Promise<{ mes: string; despesas: Despesa[]; total_despesas: number }> {
    const res = await fetch(`/api/despesas/list.php?mes=${encodeURIComponent(mes)}`, { ...defaultOptions, method: "GET" });
    return handleJson(res);
  },
  async listarDespesasPorPeriodo(
    params?: { start?: string; end?: string },
    options?: { signal?: AbortSignal },
  ): Promise<{ start: string; end: string; despesas: Despesa[]; total_despesas: number }> {
    const q = new URLSearchParams();
    if (params?.start) q.set("start", params.start);
    if (params?.end) q.set("end", params.end);
    const res = await fetch(`/api/despesas/list-by-range.php?${q.toString()}`, {
      ...defaultOptions,
      method: "GET",
      signal: options?.signal,
    });
    return handleJson(res);
  },
  async mesesDisponiveisDespesas(forward = 6): Promise<{ meses: string[] }> {
    const res = await fetch(`/api/despesas/meses-disponiveis.php?forward=${encodeURIComponent(String(forward))}`, { ...defaultOptions, method: "GET" });
    return handleJson(res);
  },
  async criarDespesa(payload: { nome: string; valor: number; tipo: "unica" | "recorrente"; classificacao: "despesa" | "compra" | "outro"; data?: string }): Promise<Despesa> {
    const res = await fetch("/api/despesas/create.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify(payload),
    });
    const data = await handleJson<Despesa>(res);
    return data;
  },
  async atualizarDespesa(payload: { id: number; nome: string; valor: number; tipo: "unica" | "recorrente"; classificacao?: "despesa" | "compra" | "outro"; data?: string }): Promise<Despesa> {
    const res = await fetch("/api/despesas/update.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify(payload),
    });
    const data = await handleJson<Despesa>(res);
    return data;
  },
  async excluirDespesa(id: number): Promise<void> {
    const res = await fetch("/api/despesas/delete.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ id }),
    });
    await handleJson(res);
  },

  // --- Fornecedor (contas a pagar) ---
  async listarFornecedorLancamentos(): Promise<{ lancamentos: FornecedorLancamento[]; valor_a_pagar: number }> {
    const res = await fetch("/api/fornecedor/list.php", { ...defaultOptions, method: "GET" });
    return handleJson(res);
  },
  async criarFornecedorLancamento(payload: {
    fornecedor: string;
    valor: number;
    data_lancamento: string;
    data_vencimento: string;
    status?: "pendente" | "pago";
    forma_pagamento?: "a_vista";
  }): Promise<FornecedorLancamento> {
    const res = await fetch("/api/fornecedor/create.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ ...payload, forma_pagamento: "a_vista" }),
    });
    return handleJson(res);
  },
  async criarFornecedorLancamentoParcelado(payload: {
    fornecedor: string;
    valor: number;
    data_lancamento: string;
    forma_pagamento: "parcelado";
    parcelas: Array<{ valor: number; data_vencimento: string }>;
  }): Promise<{ parcelamento_id: string; lancamentos: FornecedorLancamento[] }> {
    const res = await fetch("/api/fornecedor/create.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify(payload),
    });
    return handleJson(res);
  },
  async atualizarFornecedorLancamento(payload: {
    id: number;
    fornecedor: string;
    valor: number;
    data_lancamento: string;
    data_vencimento: string;
    status: "pendente" | "pago";
  }): Promise<FornecedorLancamento> {
    const res = await fetch("/api/fornecedor/update.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify(payload),
    });
    return handleJson(res);
  },
  async excluirFornecedorLancamento(id: number): Promise<void> {
    const res = await fetch("/api/fornecedor/delete.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ id }),
    });
    await handleJson(res);
  },

  /** Upload OFX (somente memória no servidor); retorna prévia com id de extrato (FITID ou hash se vazio) e duplicados. */
  async previewImportOfx(file: File): Promise<{
    total_encontrados: number;
    novos: number;
    duplicados: number;
    itens: Array<{
      fitid: string;
      data: string;
      valor: number;
      descricao: string;
      duplicado: boolean;
      saida?: "" | "despesa" | "compra" | "outro";
    }>;
  }> {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch("/api/financeiro/import-ofx.php", {
      credentials: "include",
      method: "POST",
      body: fd,
    });
    return handleJson(res);
  },

  /** Confirma importação OFX com classificação explícita por linha (despesa/compra/outro). */
  async saveImportOfx(
    itens: Array<{ fitid: string; nome: string; valor: number; data: string; saida: "despesa" | "compra" | "outro" }>,
  ): Promise<{ salvos: number; ignorados_duplicata: number; erros: string[] }> {
    const res = await fetch("/api/financeiro/save-import-ofx.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ itens }),
    });
    return handleJson(res);
  },

  async totalDesperdiciosMes(mes: string): Promise<{ mes: string; total_desperdicios: number }> {
    const res = await fetch(`/api/desperdicios/total-mes.php?mes=${encodeURIComponent(mes)}`, { ...defaultOptions, method: "GET" });
    return handleJson(res);
  },

  async listarProdutosCaixa(): Promise<Array<{ id: number; nome: string; preco_venda: number | null; possui_composicao: boolean }>> {
    const res = await fetch("/api/produtos/list-caixa.php", { ...defaultOptions, method: "GET" });
    const data = await handleJson<{ produtos: Array<{ id: number; nome: string; preco_venda: number | null; possui_composicao: boolean }> }>(res);
    return data.produtos;
  },

  async criarPromocao(payload: {
    nome: string;
    valor: number;
    tipo?: "fixa" | "configuravel" | "categoria";
    categoria_produto?: string;
    categorias?: Array<{ categoria: string; quantidade: number }>;
    itens?: Array<{ produto_id: number; quantidade: number }>;
    grupos?: Array<{ nome_grupo: string; quantidade_obrigatoria: number; produtos: Array<{ produto_id: number }> }>;
  }): Promise<{ promocao_id: number; produto_id: number; nome: string; valor: number; tipo: string }> {
    const res = await fetch("/api/promocoes/create.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify(payload),
    });
    return handleJson(res);
  },

  async listarPromocoes(): Promise<Array<Promocao>> {
    const res = await fetch("/api/promocoes/list.php", { ...defaultOptions, method: "GET" });
    const data = await handleJson<{ promocoes: Array<Promocao> }>(res);
    return data.promocoes;
  },

  async obterPromocao(id: number): Promise<Promocao> {
    const res = await fetch(`/api/promocoes/get.php?id=${id}`, { ...defaultOptions, method: "GET" });
    return handleJson(res);
  },

  async atualizarPromocao(payload: {
    id: number;
    nome: string;
    valor: number;
    tipo?: "fixa" | "configuravel" | "categoria";
    categoria_produto?: string;
    categorias?: Array<{ categoria: string; quantidade: number }>;
    itens?: Array<{ produto_id: number; quantidade: number }>;
    grupos?: Array<{ nome_grupo: string; quantidade_obrigatoria: number; produtos: Array<{ produto_id: number }> }>;
  }): Promise<{ promocao_id: number; produto_id: number; nome: string; valor: number; tipo: string }> {
    const res = await fetch("/api/promocoes/update.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify(payload),
    });
    return handleJson(res);
  },

  async excluirPromocao(id: number): Promise<void> {
    const res = await fetch("/api/promocoes/delete.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ id }),
    });
    await handleJson(res);
  },

  async obterCmvIdeal(): Promise<number | null> {
    const res = await fetch("/api/profile/cmv-ideal.php", { ...defaultOptions, method: "GET" });
    const data = await handleJson<{ cmv_ideal: number | null }>(res);
    return data.cmv_ideal ?? null;
  },

  async obterHorarioFuncionamento(): Promise<{
    horario_abertura: string | null;
    horario_encerramento: string | null;
  }> {
    const res = await fetch("/api/profile/cmv-ideal.php", { ...defaultOptions, method: "GET" });
    const data = await handleJson<{
      horario_abertura?: string | null;
      horario_encerramento?: string | null;
    }>(res);
    return {
      horario_abertura: data.horario_abertura ?? null,
      horario_encerramento: data.horario_encerramento ?? null,
    };
  },

  async obterMeuPerfil(): Promise<{
    nome: string | null;
    email: string | null;
    cpf: string | null;
    celular: string | null;
    nome_empresa: string | null;
    cnpj: string | null;
    ie: string | null;
    rua: string | null;
    numero: string | null;
    bairro: string | null;
    cidade: string | null;
    cep: string | null;
    cmv_ideal: number | null;
    horario_abertura: string | null;
    horario_encerramento: string | null;
  }> {
    const res = await fetch("/api/profile/get.php", { ...defaultOptions, method: "GET" });
    const data = await handleJson<{ perfil: {
      nome: string | null;
      email: string | null;
      cpf: string | null;
      celular: string | null;
      nome_empresa: string | null;
      cnpj: string | null;
      ie: string | null;
      rua: string | null;
      numero: string | null;
      bairro: string | null;
      cidade: string | null;
      cep: string | null;
      cmv_ideal: number | null;
      horario_abertura: string | null;
      horario_encerramento: string | null;
    } }>(res);
    return data.perfil;
  },

  async alterarSenha(payload: { senhaAtual: string; senhaNova: string }): Promise<void> {
    const res = await fetch("/api/auth/change-password.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({
        senha_atual: payload.senhaAtual,
        senha_nova: payload.senhaNova,
      }),
    });
    await handleJson(res);
  },

  async salvarMeuPerfil(payload: {
    nome?: string | null;
    email?: string | null;
    cpf?: string | null;
    celular?: string | null;
    nome_empresa?: string | null;
    cnpj?: string | null;
    ie?: string | null;
    rua?: string | null;
    numero?: string | null;
    bairro?: string | null;
    cidade?: string | null;
    cep?: string | null;
    cmv_ideal?: string | number | null;
    horario_abertura?: string | null;
    horario_encerramento?: string | null;
  }): Promise<void> {
    const res = await fetch("/api/profile/update.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify(payload),
    });
    await handleJson(res);
  },

  async listarComandasCozinha(): Promise<{ cards: ComandaCozinhaCard[] }> {
    const res = await fetch("/api/comandas/cozinha/list.php", {
      ...defaultOptions,
      method: "GET",
    });
    return handleJson<{ cards: ComandaCozinhaCard[] }>(res);
  },

  async finalizarItensComandaCozinha(itemIds: number[]): Promise<void> {
    const res = await fetch("/api/comandas/cozinha/finalizar.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ item_ids: itemIds }),
    });
    await handleJson(res);
  },

  async cadastrarEmpresaParceiraPublica(payload: Record<string, unknown>): Promise<void> {
    const res = await fetch("/api/empresas_parceiras/create.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify(payload),
    });
    await handleJson(res);
  },

  async listarEmpresasParceiras(): Promise<
    import("@/lib/empresaParceiraFields").EmpresaParceira[]
  > {
    const res = await fetch("/api/empresas_parceiras/list.php", {
      ...defaultOptions,
      method: "GET",
    });
    const data = await handleJson<{ empresas_parceiras: import("@/lib/empresaParceiraFields").EmpresaParceira[] }>(res);
    return data.empresas_parceiras ?? [];
  },

  async buscarEmpresaParceira(id: number): Promise<import("@/lib/empresaParceiraFields").EmpresaParceira> {
    const res = await fetch(`/api/empresas_parceiras/get.php?id=${encodeURIComponent(String(id))}`, {
      ...defaultOptions,
      method: "GET",
    });
    const data = await handleJson<{ empresa: import("@/lib/empresaParceiraFields").EmpresaParceira }>(res);
    return data.empresa;
  },

  async atualizarEmpresaParceira(id: number, payload: Record<string, unknown>): Promise<void> {
    const res = await fetch("/api/empresas_parceiras/update.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ id, ...payload }),
    });
    await handleJson(res);
  },

  async excluirEmpresaParceira(id: number): Promise<void> {
    const res = await fetch("/api/empresas_parceiras/delete.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ id }),
    });
    await handleJson(res);
  },

  async aiStockCreateSession(): Promise<{
    session_id: number;
    token: string;
    capture_url: string;
    expires_at: string;
  }> {
    const res = await fetch("/api/estoque/ai/session-create.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({}),
    });
    return handleJson(res);
  },

  async aiStockSessionStatus(sessionId: number): Promise<{
    session_id: number;
    status: string;
    expires_at: string;
    itens?: AiStockAnalyzedItem[];
    error?: string;
  }> {
    const res = await fetch(`/api/estoque/ai/session-status.php?session_id=${sessionId}`, {
      ...defaultOptions,
      method: "GET",
    });
    return handleJson(res);
  },

  async aiStockCancelSession(sessionId: number): Promise<void> {
    const res = await fetch("/api/estoque/ai/session-cancel.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({ session_id: sessionId }),
    });
    await handleJson(res);
  },

  async aiStockAnalyzeImage(file: File): Promise<{ itens: AiStockAnalyzedItem[] }> {
    const fd = new FormData();
    fd.append("image", file);
    const res = await fetch("/api/estoque/ai/analyze.php", {
      credentials: "include",
      method: "POST",
      body: fd,
    });
    return handleJson(res);
  },

  async aiStockConfirmEntradas(
    itens: Array<{
      nome_lido: string;
      item_tipo: "produto" | "insumo";
      item_id: number;
      quantidade: number;
      unidade_lida: string;
      valor_pago: number;
    }>,
    sessionId?: number | null,
  ): Promise<{ itens: Array<{ tipo: string; id: number; quantidadeAtual: number; valorAtual: number | null }> }> {
    const res = await fetch("/api/estoque/ai/confirm.php", {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify({
        itens,
        session_id: sessionId ?? undefined,
      }),
    });
    return handleJson(res);
  },

  async aiStockMobileInfo(token: string): Promise<{
    status: string;
    expires_at: string;
    primary_color: string;
    name: string;
    can_upload: boolean;
    error?: string | null;
  }> {
    const res = await fetch(`/api/estoque/ai/mobile-info.php?token=${encodeURIComponent(token)}`, {
      method: "GET",
      credentials: "omit",
      headers: { Accept: "application/json" },
    });
    return handleJson(res);
  },

  async aiStockMobileUpload(token: string, file: File): Promise<{ status: string; itens?: AiStockAnalyzedItem[] }> {
    const fd = new FormData();
    fd.append("token", token);
    fd.append("image", file);
    const res = await fetch("/api/estoque/ai/mobile-upload.php", {
      method: "POST",
      credentials: "omit",
      body: fd,
    });
    return handleJson(res);
  },
};

export interface ComandaCozinhaItem {
  id: number;
  nome_produto: string;
  quantidade: number;
  observacao?: string | null;
}

export interface ComandaCozinhaCard {
  numero_comanda?: string | null;
  numero_mesa?: string | null;
  comanda_id: number;
  nome: string;
  mesa?: string;
  cliente_nome?: string | null;
  cliente_whatsapp?: string | null;
  origem?: string | null;
  iniciado_em: string;
  itens: ComandaCozinhaItem[];
}

export { POST_LOGIN_REDIRECT_KEY } from "@/lib/authRedirect";

export interface PromocaoItem {
  produto_id: number;
  produto_nome: string;
  quantidade: number;
}

export interface PromocaoGrupo {
  nome_grupo: string;
  quantidade_obrigatoria: number;
  produtos: Array<{ produto_id: number; produto_nome?: string }>;
}

export interface Promocao {
  id: number;
  nome: string;
  valor: number;
  produto_id: number | null;
  tipo?: "fixa" | "configuravel" | "categoria";
  /** Legado: uma única categoria */
  categoria_produto?: string | null;
  /** Promoção por categoria: várias linhas categoria + quantidade */
  categorias?: Array<{ categoria: string; quantidade: number }>;
  itens: Array<PromocaoItem>;
  grupos?: Array<PromocaoGrupo>;
}

export interface VendaItem {
  venda_id: number;
  produto_id: number | null;
  nome_produto: string;
  quantidade: number;
  preco_unitario: number;
  subtotal: number;
}

export interface DesperdicioItemRow {
  produto_id: number;
  nome_produto: string;
  quantidade: number;
  preco_unitario: number;
  subtotal: number;
}

export interface DesperdicioListItem {
  id: number;
  tipo_item: 'insumo' | 'produto' | 'carrinho';
  motivo: string;
  observacao: string | null;
  createdAt: string;
  total: number;
  responsavel_nome: string | null;
  itens: DesperdicioItemRow[];
}

export interface Venda {
  atendimento?: {
    tipo: "mesa" | "comanda" | "ambos";
    numero_comanda?: string;
    numero_mesa?: string;
    numero: string;
    nome: string;
    identificacao: string;
  } | null;
  id: number | string;
  user_id: number | null;
  total: number;
  pagamento_json?: string | null;
  pagamento: { pix?: number; dinheiro?: number; credito?: number; debito?: number };
  nfce_documento?: string | null;
  nfce_chave?: string | null;
  nfce_emitida_em?: string | null;
  createdAt: string;
  itens: VendaItem[];
}

export interface Despesa {
  id: number;
  nome: string;
  valor: number;
  tipo: "unica" | "recorrente";
  classificacao: "despesa" | "compra" | "outro";
  data: string | null;
  inicio_mes: string | null;
  origem: "manual" | "caixa_retirada" | "extrato_ofx";
  origemId: number | null;
  /** FITID do OFX (importação); único quando preenchido */
  id_extrato?: string | null;
  createdAt?: string;
  updatedAt?: string | null;
}

export interface FornecedorLancamento {
  id: number;
  fornecedor: string;
  valor: number;
  data_lancamento: string;
  data_vencimento: string;
  status: "pendente" | "pago";
  forma_pagamento?: "a_vista" | "parcelado";
  parcelamento_id?: string | null;
  parcela_numero?: number | null;
  parcela_total?: number | null;
  createdAt?: string;
  updatedAt?: string | null;
}

export type AiStockItemStatus = "identificado" | "revisar" | "nao_encontrado";

export type AiStockAnalyzedItem = {
  nome_lido: string;
  quantidade: number | null;
  unidade_lida: string | null;
  unidade_bruta?: string | null;
  valor_pago: number | null;
  status: AiStockItemStatus;
  item_tipo: "produto" | "insumo" | null;
  item_id: number | null;
  item_nome: string | null;
  item_unidade: string | null;
};
